import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, FileText, Shield, Cookie, Server } from "lucide-react";
import Footer from "@/components/landing/Footer";

const PAGE_META = {
    regulamin: { icon: FileText, titles: { pl: "Regulamin", en: "Terms of Service", ua: "Умови користування" } },
    privacy: { icon: Shield, titles: { pl: "Polityka Prywatności", en: "Privacy Policy", ua: "Політика приватності" } },
    cookies: { icon: Cookie, titles: { pl: "Polityka Cookies", en: "Cookie Policy", ua: "Політика Cookies" } },
    subprocessors: { icon: Server, titles: { en: "Sub-processors" } },
};

const LANG_LABELS = { pl: "Polski", en: "English", ua: "Українська" };

const BACK_LABEL = {
    pl: "Powrót do strony głównej",
    en: "Back to home",
    ua: "Назад до головної",
};

/**
 * LegalPage — renders Markdown legal documents from /legal/*.md (public dir).
 *
 * Props:
 *   - slug: 'regulamin' | 'privacy' | 'cookies' | 'subprocessors'
 *   - multilingual: bool — if true, supports lang switch (PL/EN/UA); else fixed EN
 */
const LegalPage = ({ slug, multilingual = true }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const lang = useMemo(() => {
        if (!multilingual) return "en";
        const requested = searchParams.get("lang");
        return ["pl", "en", "ua"].includes(requested) ? requested : "pl";
    }, [searchParams, multilingual]);

    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const meta = PAGE_META[slug] || {};
    const Icon = meta.icon || FileText;
    const pageTitle = meta.titles?.[lang] || meta.titles?.en || slug;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const filename = multilingual ? `${slug}.${lang}.md` : `${slug}.md`;
        const url = `/legal/${filename}`;

        fetch(url)
            .then((resp) => {
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                return resp.text();
            })
            .then((text) => {
                if (cancelled) return;
                // Strip YAML frontmatter
                const stripped = text.replace(/^---\n[\s\S]*?\n---\n/, "").trim();
                setContent(stripped);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Failed to load legal content:", url, err);
                setError(err.message || "Failed to load document");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [slug, lang, multilingual]);

    const handleLangChange = (newLang) => {
        const next = new URLSearchParams(searchParams);
        next.set("lang", newLang);
        setSearchParams(next, { replace: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        document.title = `${pageTitle} · AutoAds`;
    }, [pageTitle]);

    return (
        <div className="min-h-screen bg-background" data-testid={`legal-page-${slug}`}>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                <div className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-secondary-c hover:text-text-primary transition mb-6"
                        data-testid="legal-back-link"
                    >
                        <ArrowLeft size={16} />
                        <span>{BACK_LABEL[lang] || BACK_LABEL.en}</span>
                    </Link>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-primary-c/10 flex items-center justify-center">
                            <Icon size={20} className="text-primary-c" />
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">{pageTitle}</h1>
                    </div>

                    {multilingual && (
                        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Language">
                            {["pl", "en", "ua"].map((code) => (
                                <button
                                    key={code}
                                    type="button"
                                    role="tab"
                                    aria-selected={lang === code}
                                    onClick={() => handleLangChange(code)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition border ${
                                        lang === code
                                            ? "bg-primary-c text-white border-primary-c"
                                            : "bg-transparent text-secondary-c border-border hover:border-primary-c/40 hover:text-text-primary"
                                    }`}
                                    data-testid={`lang-switch-${code}`}
                                >
                                    {LANG_LABELS[code]}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <article className="legal-prose" data-testid="legal-content">
                    {loading && (
                        <div className="space-y-3" data-testid="legal-loading">
                            <div className="h-4 bg-border/40 rounded animate-pulse w-3/4" />
                            <div className="h-4 bg-border/40 rounded animate-pulse w-full" />
                            <div className="h-4 bg-border/40 rounded animate-pulse w-5/6" />
                            <div className="h-4 bg-border/40 rounded animate-pulse w-2/3" />
                        </div>
                    )}

                    {error && (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-400" data-testid="legal-error">
                            {error}
                        </div>
                    )}

                    {!loading && !error && (
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                a: ({ node, ...props }) => (
                                    <a
                                        {...props}
                                        className="text-primary-c hover:underline"
                                        target={props.href?.startsWith("http") ? "_blank" : undefined}
                                        rel={props.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                                    />
                                ),
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                    )}
                </article>
            </div>
            <Footer />
        </div>
    );
};

export default LegalPage;
